import { cn } from '@/lib/utils';

/**
 * The one-line fact strip that opens a settings tab.
 *
 * Every tab on Settings > Reviews leads with the same strip so the three read
 * as one screen rather than three screens behind one set of tabs: the same
 * 11px uppercase label, the same 14px value, the same card edge. It is a
 * HEADER, not a second thing to read — four facts at most, each short enough
 * to survive `truncate` on a narrow cell.
 *
 * `tabular-nums` is load-bearing: without it a count changing on refetch
 * re-measures the cell and the whole row shifts.
 */
export interface SettingsSummaryStat {
  label: string;
  value: string;
  /** Amber, matching `IntegrationHealth['attention']`. Use only for "someone must act". */
  tone?: 'default' | 'attention';
}

// Static, because Tailwind generates classes by scanning source text — a
// template-built `sm:grid-cols-${n}` is never emitted and silently collapses
// the row to one column.
const COLUMN_CLASSES: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
};

export function SettingsSummaryRow({
  stats,
  className,
}: {
  stats: SettingsSummaryStat[];
  className?: string;
}) {
  if (stats.length === 0) return null;
  return (
    <dl
      className={cn(
        'grid grid-cols-2 overflow-hidden rounded-xl border bg-card shadow-sm sm:divide-x sm:divide-border/60',
        COLUMN_CLASSES[stats.length] ?? 'sm:grid-cols-4',
        className,
      )}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="px-5 py-3.5">
          <dt className="text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-muted-foreground/70">
            {stat.label}
          </dt>
          <dd
            className={cn(
              'mt-1 truncate text-sm font-semibold tabular-nums',
              stat.tone === 'attention' ? 'text-amber-700 dark:text-amber-400' : 'text-foreground',
            )}
          >
            {stat.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
